import { useEffect, useRef } from 'react'
import { theme } from 'antd'

/**
 * Tarmoq foni — sekin siljiydigan tugunlar va ular orasidagi chiziqlar.
 *
 * <h2>Nega bu bor</h2>
 * Kirish ekrani — mahsulot haqidagi BIRINCHI taassurot. Bo'sh kulrang fon
 * ustidagi ikkita maydon "ichki skript" taassurotini beradi, holbuki bu
 * abonentning puli va ulanishi turgan kabinet. Fon shu ishning o'zini
 * ko'rsatadi: tugunlar va ular orasidagi bog'lanishlar.
 *
 * <h2>⚠️ Admin panelidagi nusxasi bilan bir xil — ATAYLAB</h2>
 * Uch panel mustaqil ilova (umumiy paket yo'q), `LanguageSwitcher` va
 * `ThemeToggle` ham shunday nusxalangan. Umumiy paket chiqarish faqat shu
 * bitta komponent uchun uch ilovaning qurish zanjirini bog'lab qo'yardi.
 * O'zgartirsangiz ikkalasini ham ko'ring.
 *
 * <h2>⚠️ HARAKATNI KAMAYTIRISH SO'ROVI HURMAT QILINADI</h2>
 * `prefers-reduced-motion: reduce` bo'lsa animatsiya UMUMAN ishga
 * tushmaydi — bitta statik kadr chiziladi. Vestibulyar buzilishi bor
 * odam uchun doimiy harakatlanuvchi fon ekranni ishlatib bo'lmaydigan
 * qiladi, va bu sozlama aynan shuning uchun mavjud.
 *
 * <h2>⚠️ Ranglar mavzu tokenidan</h2>
 * Qotirilgan rang yorug' rejimda ko'rinmay ketardi yoki qorong'ida
 * ko'zni qamashtirardi. Token o'zgarganda fon o'zi moslashadi.
 *
 * <h2>⚠️ Nega canvas, SVG emas</h2>
 * 60 ta tugun va ular orasidagi ~200 chiziq har kadrda qayta chiziladi.
 * SVG'da bu 260 ta DOM tugunini har kadrda yangilash demak — brauzer
 * layout'ni qayta hisoblaydi va sekin mashinada login ekrani "qotib"
 * qoladi. Canvas'da esa bitta element.
 */
interface NetworkBackdropProps {
  /** Tugunlar soni — kichik ekranda kamroq. Standart 46. */
  nodeCount?: number
  /** Fon shaffofligi (0..1). Matn o'qilishi kerak, fon esa sezilishi. */
  opacity?: number
}

interface Node {
  x: number
  y: number
  vx: number
  vy: number
  /** Yiriklar — NAS, mayda — abonent. Faqat vizual farq. */
  r: number
}

/** Ikki tugun orasida chiziq chiziladigan eng katta masofa (px) */
const LINK_DISTANCE = 150

export function NetworkBackdrop({ nodeCount = 46, opacity = 0.75 }: NetworkBackdropProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const { token } = theme.useToken()

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    let width = 0
    let height = 0
    let nodes: Node[] = []
    let frame = 0

    /**
     * ⚠️ `devicePixelRatio` hisobga olinadi — usiz Retina ekranda
     * chiziqlar xira bo'lib ko'rinardi.
     */
    const resize = () => {
      const rect = canvas.getBoundingClientRect()
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      width = rect.width
      height = rect.height
      canvas.width = Math.max(1, Math.floor(width * dpr))
      canvas.height = Math.max(1, Math.floor(height * dpr))
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }

    const seed = () => {
      // ⚠️ Tor ekranda tugunlar soni kamaytiriladi: bir xil son telefon
      // ekranida "qorishgan to'r" ko'rinishini berardi.
      const count = width < 600 ? Math.round(nodeCount * 0.5) : nodeCount
      nodes = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        // Tezlik ATAYLAB juda kichik: fon e'tiborni tortmasligi kerak
        vx: (Math.random() - 0.5) * 0.22,
        vy: (Math.random() - 0.5) * 0.22,
        r: Math.random() < 0.15 ? 2.6 : 1.4,
      }))
    }

    const draw = () => {
      ctx.clearRect(0, 0, width, height)

      // Avval chiziqlar, keyin tugunlar — tugunlar chiziq ustida turadi
      for (let i = 0; i < nodes.length; i += 1) {
        const a = nodes[i]
        if (!a) continue
        for (let j = i + 1; j < nodes.length; j += 1) {
          const b = nodes[j]
          if (!b) continue
          const dx = a.x - b.x
          const dy = a.y - b.y
          const dist = Math.hypot(dx, dy)
          if (dist > LINK_DISTANCE) continue
          // Uzoqroq bog'lanish — xiraroq: to'r "nafas olayotgandek" ko'rinadi
          ctx.globalAlpha = (1 - dist / LINK_DISTANCE) * 0.35
          ctx.strokeStyle = token.colorPrimary
          ctx.lineWidth = 1
          ctx.beginPath()
          ctx.moveTo(a.x, a.y)
          ctx.lineTo(b.x, b.y)
          ctx.stroke()
        }
      }

      ctx.globalAlpha = 0.75
      ctx.fillStyle = token.colorPrimary
      for (const n of nodes) {
        ctx.beginPath()
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2)
        ctx.fill()
      }
      ctx.globalAlpha = 1
    }

    const step = () => {
      for (const n of nodes) {
        n.x += n.vx
        n.y += n.vy
        // Chekkadan qaytish — tugunlar ekrandan chiqib ketmasin
        if (n.x < 0 || n.x > width) n.vx *= -1
        if (n.y < 0 || n.y > height) n.vy *= -1
      }
      draw()
      frame = requestAnimationFrame(step)
    }

    const onResize = () => {
      resize()
      seed()
      draw()
    }

    resize()
    seed()
    if (reduced) {
      draw()
    } else {
      frame = requestAnimationFrame(step)
    }
    window.addEventListener('resize', onResize)

    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('resize', onResize)
    }
  }, [nodeCount, token.colorPrimary])

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        opacity,
        // Fon bosilmaydi — ustidagi forma bilan ishlashga xalaqit bermasin
        pointerEvents: 'none',
      }}
    />
  )
}
